import { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Dashboard } from './pages/Dashboard';
import { NewGame } from './pages/NewGame';
import { Workspace } from './pages/Workspace';
import { Settings } from './pages/Settings';
import { Devices } from './pages/Devices';
import { AppModeLauncher } from './pages/AppModeLauncher';
import { CloudDesktopShell } from './pages/CloudDesktopShell';
import { UpdateBanner } from './components/UpdateBanner';
import { WhatsNewCard } from './components/WhatsNewCard';
import { AppFrameProvider, type AppFrame } from './AppFrameContext';

export function appRootClassName(frame: AppFrame = 'fullscreen'): string {
  const sizeClass = frame === 'fullscreen' ? 'h-screen w-screen' : 'h-full w-full';
  return `flex ${sizeClass} flex-col overflow-hidden bg-crucible-bg text-crucible-fg`;
}

/** Fake macOS traffic-light buttons for the browser preview.
 *  Renders only when the mock IPC bridge is active (i.e. not real Electron).
 *  Positioned to match the Electron trafficLightPosition: { x: 24, y: 31 }. */
export function MockTrafficLights(): React.JSX.Element | null {
  // Real Electron exposes electronAPI; the mock bridge sets a sentinel flag
  const isMock =
    typeof (window as unknown as { __reactErrors?: unknown }).__reactErrors !== 'undefined' &&
    !(window as unknown as { electronAPI?: unknown }).electronAPI;
  if (!isMock) return null;
  return (
    <div className="fixed z-[9999] flex items-center gap-2" style={{ left: 24, top: 31 }}>
      <div className="h-3.5 w-3.5 rounded-full bg-[#FF5F57]" />
      <div className="h-3.5 w-3.5 rounded-full bg-[#FEBC2E]" />
      <div className="h-3.5 w-3.5 rounded-full bg-[#28C840]" />
    </div>
  );
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
    },
  },
});

/** Apply theme and feature-flag CSS classes to document root. */
function ThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { data: config } = useQuery({
    queryKey: ['config:get'],
    queryFn: () => window.crucible.invoke('config:get'),
    staleTime: 60_000,
  });

  const { data: flags } = useQuery({
    queryKey: ['app:get-feature-flags'],
    queryFn: () => window.crucible.invoke('app:get-feature-flags'),
    staleTime: 60_000,
  });

  useEffect(() => {
    const theme = config?.theme ?? 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, [config?.theme]);

  useEffect(() => {
    if (flags?.newUi) {
      document.documentElement.classList.add('new-ui');
    } else if (flags !== undefined) {
      document.documentElement.classList.remove('new-ui');
    }
  }, [flags?.newUi, flags]);

  return <>{children}</>;
}

export function App({ frame = 'fullscreen' }: { frame?: AppFrame } = {}): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppFrameProvider frame={frame}>
          <HashRouter>
            <div className={appRootClassName(frame)}>
              <MockTrafficLights />
              <UpdateBanner fromAppShell />
              {/* "What's new" card — mounted at the App shell so it surfaces
              regardless of which route the user lands on (Dashboard,
              Workspace, Settings, Devices). The component self-suppresses
              when the main process returns null (fresh install, no notes
              for this version, already dismissed). Floats top-right so it
              doesn't displace any route's layout. See issue #293. */}
              <WhatsNewCard />
              <div className="flex-1 overflow-hidden">
                <Routes>
                  {/* /onboarding (pages/Onboarding.tsx) was an unreachable second
                  onboarding flow that the audit flagged as dead code (see
                  docs/board-member-audit-current-state.md §1, "Other
                  landmines"). The reachable onboarding is the
                  <OnboardingOverlay/> that Dashboard.tsx mounts on first
                  launch — this PR removes the duplicate to stop the two
                  diverging. */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/new-game" element={<NewGame />} />
                  <Route path="/workspace/:gameId" element={<Workspace />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/devices" element={<Devices />} />
                  <Route path="/app-mode" element={<AppModeLauncher />} />
                  <Route path="/cloud-shell" element={<CloudDesktopShell />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </div>
          </HashRouter>
        </AppFrameProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
